import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../App';
import { Box, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Typography, Divider, IconButton, Menu, MenuItem, Avatar } from '@mui/material';
import { ChangePasswordDialog } from './ChangePasswordDialog';

// Import all the necessary icons
import DashboardIcon from '@mui/icons-material/Dashboard';
import SettingsIcon from '@mui/icons-material/Settings';
import NotificationsIcon from '@mui/icons-material/Notifications';
import BarChartIcon from '@mui/icons-material/BarChart';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import FacebookIcon from '@mui/icons-material/Facebook';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import StorageIcon from '@mui/icons-material/Storage';
import LabelIcon from '@mui/icons-material/Label';
import ArticleIcon from '@mui/icons-material/Article';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import LogoutIcon from '@mui/icons-material/Logout';
import InsightsIcon from '@mui/icons-material/Insights';
import LockResetIcon from '@mui/icons-material/LockReset';

const Sidebar = () => {
    const { user, logout } = useAuth();
    const { hasAccess } = usePermissions();
    
    // --- State for the new dropdown menu ---
    const [anchorEl, setAnchorEl] = useState(null);
    const menuOpen = Boolean(anchorEl);

    // State for the password dialog
    const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);

    const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
    const handleMenuClose = () => setAnchorEl(null);

    const handleChangePassword = () => {
        setPasswordDialogOpen(true);
        handleMenuClose();
    };

    const handleLogout = () => {
        logout();
        handleMenuClose();
    };

    const navItems = [
        { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
        { text: 'Insights', icon: <InsightsIcon />, path: '/insights' },
        { text: 'Keywords', icon: <LabelIcon />, path: '/keywords' },
        { text: 'Mentions', icon: <ArticleIcon />, path: '/mentions' },
        { text: 'Sentiment', icon: <BarChartIcon />, path: '/sentiment' },
        { text: 'Trends', icon: <TrendingUpIcon />, path: '/trends' },
        { text: 'News', icon: <NewspaperIcon />, path: '/news' },
        { text: 'Facebook Monitoring', icon: <FacebookIcon />, path: '/facebook-monitoring' },
        { text: 'Raw Data View', icon: <StorageIcon />, path: '/raw-data-view' },
        { text: 'Alerts', icon: <NotificationsIcon />, path: '/alerts' },
        { text: 'Simulation', icon: <PlayCircleOutlineIcon />, path: '/simulation' },
        { text: 'Configuration', icon: <SettingsIcon />, path: '/configuration' },
        { text: 'Permissions', icon: <VpnKeyIcon />, path: '/permissions' },
    ];

      // --- THIS IS THE FIX ---
    const navLinkStyles = ({ isActive }) => ({
        // 1. Set the active background color to black
        backgroundColor: isActive ? '#111827' : 'transparent', // Using a dark tailwind grey, but 'black' also works
        // 2. Set the active text and icon color to white for contrast
        color: isActive ? '#f9fafb' : '#475569',
        fontWeight: isActive ? 600 : 400,
        textDecoration: 'none',
        display: 'block',
        borderRadius: '8px',
        marginBottom: '4px'
    });

    return (
        <>
            <Box sx={{ width: 250, height: '100vh', bgcolor: 'background.paper', borderRight: '1px solid #ddd', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ p: 2, borderBottom: '1px solid #ddd', textAlign: 'center' }}>
                    <Typography variant="h6" component="div">SocialAI (EYE)</Typography>
                </Box>
                <List sx={{ p: 1, flexGrow: 1 }}>
                    {navItems.map((item) => {
                        if (!hasAccess(item.text)) return null;
                        return (
                            <NavLink to={item.path} key={item.text} style={navLinkStyles}>
                                <ListItem disablePadding>
                                    <ListItemButton>
                                        <ListItemIcon sx={{color: 'inherit'}}>{item.icon}</ListItemIcon>
                                        <ListItemText primary={item.text} />
                                    </ListItemButton>
                                </ListItem>
                            </NavLink>
                        );
                    })}
                </List>
                
                {/* --- User menu at the bottom --- */}
                <Box sx={{ p: 1, borderTop: '1px solid #ddd' }}>
                    <ListItemButton onClick={handleMenuOpen} sx={{borderRadius: 1}}>
                        <ListItemIcon>
                            <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>{user?.email[0].toUpperCase()}</Avatar>
                        </ListItemIcon>
                        <ListItemText primary={user?.email} sx={{overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}/>
                    </ListItemButton>
                </Box>
            </Box>

            {/* --- Dropdown Menu --- */}
            <Menu anchorEl={anchorEl} open={menuOpen} onClose={handleMenuClose} anchorOrigin={{ vertical: 'top', horizontal: 'right' }} transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                <MenuItem onClick={handleChangePassword}>
                    <ListItemIcon><LockResetIcon fontSize="small" /></ListItemIcon>
                    Change Password
                </MenuItem>
                <MenuItem onClick={handleLogout}>
                    <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
                    Logout
                </MenuItem>
            </Menu>

            <ChangePasswordDialog
                open={passwordDialogOpen}
                onClose={() => setPasswordDialogOpen(false)}
                userToEdit={user}
                isAdminMode={false}
            />
        </>
    );
};

export default Sidebar;